import React, {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import styled, { createGlobalStyle } from "styled-components";

import { Button } from "../../components/Button";
import { Checkbox } from "../../components/Checkbox";
import { Dialog } from "../../components/Dialog";
import { GitHubIcon, GitIcon, GitLabIcon } from "../../components/Icons";
import { Label } from "../../components/Label";
import { Select } from "../../components/Select";
import { SelectTrigger } from "../../components/SelectTrigger";
import { Body2 } from "../../components/Typography";

/**
 * "Connect Repository" — the project-side counterpart to the "Connect Project"
 * dialog on the Repositories page (Apps & Integrations). Same operation from
 * the other end: pick a repository, choose which features to enable, and make
 * the connection. Built as a shared component because more than one story links
 * a repository this way.
 *
 * Also exports `RepositorySelect`, the grouped repository picker (repositories
 * grouped under their connection name, everything sorted by name, filterable
 * via the built-in search) reused wherever a repository is chosen.
 */

const SANS =
  '-apple-system, BlinkMacSystemFont, "Roboto", "Helvetica Neue", Arial, sans-serif';

/* ---- Data model + mock data ----------------------------------------- */

export type ConnectionService = "github" | "gitlab";

export interface Connection {
  id: string;
  /** Org / group name, e.g. "ActiveCollab" or "a51-ops". */
  name: string;
  service: ConnectionService;
}

/** Human-readable provider label used in the group header "name (type)". */
export const serviceLabel = (service: ConnectionService): string =>
  service === "github" ? "GitHub" : "GitLab";

/** Provider glyph for a connection: GitHub / GitLab, Git for anything else. */
export const connectionIcon = (service?: ConnectionService): ReactElement => {
  if (service === "github") {
    return <GitHubIcon />;
  }
  if (service === "gitlab") {
    return <GitLabIcon />;
  }
  return <GitIcon />;
};

export interface Repository {
  id: string;
  /** Full repository path, e.g. activecollab/backend. */
  name: string;
  connectionId: string;
}

export const CONNECTIONS: Connection[] = [
  { id: "gh-ac", name: "ActiveCollab", service: "github" },
  { id: "gl-ops", name: "a51-ops", service: "gitlab" },
];

/** Repositories already connected to the project (shown in link pickers). */
export const PROJECT_REPOSITORIES: Repository[] = [
  { id: "backend", name: "activecollab/backend", connectionId: "gh-ac" },
  { id: "frontend", name: "activecollab/frontend", connectionId: "gh-ac" },
  {
    id: "infra",
    name: "activecollab/infrastructure",
    connectionId: "gl-ops",
  },
];

/** Repositories available to connect (not yet linked to the project). */
export const CONNECTABLE_REPOSITORIES: Repository[] = [
  { id: "mobile", name: "activecollab/mobile", connectionId: "gh-ac" },
  {
    id: "design-system",
    name: "activecollab/design-system",
    connectionId: "gh-ac",
  },
  {
    id: "deploy-scripts",
    name: "a51-ops/deploy-scripts",
    connectionId: "gl-ops",
  },
  { id: "runners", name: "a51-ops/ci-runners", connectionId: "gl-ops" },
];

/**
 * Build grouped Select options — groups and options both sorted by name. Each
 * repository option carries a leading provider icon (GitHub / GitLab / Git) via
 * the Select's `icon` slot; the group header is just the connection name, since
 * the icon now conveys the service (no "(GitHub)" / "(GitLab)" suffix).
 */
export const buildRepoOptions = (
  repositories: Repository[],
  connections: Connection[]
) =>
  [...connections]
    .map((c) => ({
      id: c.id,
      name: c.name,
      options: repositories
        .filter((r) => r.connectionId === c.id)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((r) => ({
          id: r.id,
          name: r.name,
          icon: connectionIcon(c.service),
        })),
    }))
    .filter((group) => group.options.length > 0)
    .sort((a, b) => a.name.localeCompare(b.name));

/* ---- RepositorySelect ------------------------------------------------ */

/**
 * The grouped repository menu renders through a portal, so the "match the
 * trigger width" override has to be global, scoped to this instance's class.
 */
const RepoMenuGlobalStyle = createGlobalStyle<{ $cls: string; $w?: number }>`
  .c-select.${(p) => p.$cls} {
    ${(p) =>
      p.$w ? `width: ${p.$w}px !important; max-width: none !important;` : ""}
  }
`;

const RepoSelectWrap = styled.div`
  font-family: ${SANS};

  .repo-trigger {
    width: 100%;
    max-width: none;
  }
`;

export interface RepositorySelectProps {
  repositories: Repository[];
  connections: Connection[];
  selected: string | null;
  onChange: (id: string) => void;
  invalid?: boolean;
  placeholder?: string;
  className?: string;
}

export const RepositorySelect = ({
  repositories,
  connections,
  selected,
  onChange,
  invalid,
  placeholder = "Choose repository",
  className,
}: RepositorySelectProps): ReactElement => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [menuWidth, setMenuWidth] = useState<number | undefined>();
  const [menuCls] = useState(
    () => `repo-select-menu-${Math.random().toString(36).slice(2)}`
  );

  const measure = useCallback(() => {
    if (wrapRef.current) {
      setMenuWidth(wrapRef.current.offsetWidth);
    }
  }, []);

  useEffect(() => {
    measure();
  }, [measure]);

  const options = buildRepoOptions(repositories, connections);
  const selectedName =
    repositories.find((r) => r.id === selected)?.name ?? placeholder;

  return (
    <RepoSelectWrap ref={wrapRef} className={className}>
      <RepoMenuGlobalStyle $cls={menuCls} $w={menuWidth} />
      <Select
        options={options}
        selected={selected === null ? undefined : selected}
        onChange={(v) => onChange(String(v))}
        placeholder="Search repositories"
        selectClassName={menuCls}
        onSelectOpen={measure}
        forceCloseMenu
        target={
          <SelectTrigger
            className="repo-trigger"
            invalid={invalid}
            typographyProps={
              selected === null ? { color: "tertiary" } : undefined
            }
          >
            {selectedName}
          </SelectTrigger>
        }
      />
    </RepoSelectWrap>
  );
};

/* ---- ConnectRepositoryDialog ---------------------------------------- */

const StyledConnectBody = styled.div`
  font-family: ${SANS};

  .cr-field {
    margin-bottom: 16px;
  }
  .cr-label {
    display: block;
    margin-bottom: 6px;
  }
  .cr-check {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .cr-check-hint {
    margin-top: 6px;
  }
`;

export interface ConnectRepositoryDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called with the connected repository when the user confirms. */
  onConnect: (repository: Repository) => void;
  /** Repositories that can be connected (defaults to the mock connectable set). */
  repositories?: Repository[];
  connections?: Connection[];
}

export const ConnectRepositoryDialog = ({
  open,
  onClose,
  onConnect,
  repositories = CONNECTABLE_REPOSITORIES,
  connections = CONNECTIONS,
}: ConnectRepositoryDialogProps): ReactElement => {
  const [selected, setSelected] = useState<string | null>(null);
  const [issueSync, setIssueSync] = useState(false);
  const [attempted, setAttempted] = useState(false);

  // Reset the form each time the dialog opens.
  useEffect(() => {
    if (open) {
      setSelected(null);
      setIssueSync(false);
      setAttempted(false);
    }
  }, [open]);

  const invalid = attempted && selected === null;

  const handleConnect = () => {
    const repo = repositories.find((r) => r.id === selected);
    if (!repo) {
      setAttempted(true);
      return;
    }
    onConnect(repo);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} disableCloseOnEsc>
      <Dialog.Title>Connect Repository</Dialog.Title>
      <Dialog.ContentDivider />
      <Dialog.Content>
        <StyledConnectBody>
          <div className="cr-field">
            <Label size="small" className="cr-label" invalid={invalid}>
              Repository
            </Label>
            <RepositorySelect
              repositories={repositories}
              connections={connections}
              selected={selected}
              onChange={setSelected}
              invalid={invalid}
            />
          </div>

          <div className="cr-check">
            <Checkbox
              id="cr-issue-sync"
              checked={issueSync}
              onChange={(e) => setIssueSync(e.target.checked)}
            />
            <Label htmlFor="cr-issue-sync" weight="regular">
              Enable issue sync
            </Label>
          </div>
          <Body2 className="cr-check-hint" color="tertiary">
            Issues from this repository will create and update tasks in this
            project.
          </Body2>
        </StyledConnectBody>
      </Dialog.Content>
      <Dialog.ContentDivider />
      <Dialog.Actions>
        <Button
          variant="primary"
          style={{ marginRight: 12 }}
          onClick={handleConnect}
        >
          Connect
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};
